import React, { useCallback, useContext, useEffect } from 'react';
import { Edit, Delete, Save } from '@mui/icons-material';
import moment from 'moment';
import { DatePicker } from '@mui/lab';
import * as Sentry from '@sentry/browser';

import LoggedInUserContext from '../../../context/LoggedInUserContext';
import useComponentMounted from '../../../hooks/useComponentMounted';
import CheckInTheme from '../../../Model/CheckInTheme';
import { DateFormat } from '../../../utils/date';
import useToast from '../../hooks/useToast';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useCheckInTheme } from './hooks/useCheckInTheme';
import {
  Container,
  FormPanel,
  ThemesPanel,
  DatePickerContainer,
  StyledTextField,
  StyledTextarea,
  StyledButton,
  ThemeCard,
  ThemeCardActionsRow,
  ThemeTitle,
  ThemeDate,
  ThemeDescription,
  ActionButtons,
  EditButton,
  DeleteButton,
  EmptyState,
  ButtonContainer,
  ActiveChip,
} from './styles';
import texts from './texts.json';

const CheckInThemes = () => {
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { state, actions } = useCheckInTheme();
  const { userId } = useContext(LoggedInUserContext);

  const {
    startDate,
    endDate,
    themeTitle,
    themeDescription,
    isLoading,
    isLoadingThemes,
    themes,
    showConfirmDeleteModal,
    themeToDeleteId,
    themeIdToEdit,
  } = state;

  const {
    setStartDate,
    setEndDate,
    setThemeTitle,
    setThemeDescription,
    setLoading,
    setLoadingThemes,
    setThemes,
    setConfirmDeleteModal,
    setThemeToDeleteId,
    resetForm,
    setThemeIdToEdit,
  } = actions;

  // Function to fetch themes from Firebase
  const fetchThemes = useCallback(async () => {
    setLoadingThemes(true);
    try {
      const themesCollection = await CheckInTheme.getAllThemes();
      if (isComponentMountedRef.current) {
        // filter archived themes
        const unarchivedThemes = themesCollection.docs.filter((theme) => !theme.isArchived);
        const sortedThemes = unarchivedThemes.sort((a, b) => (
          b.startDate.toDate().getTime() - a.startDate.toDate().getTime()
        ));
        setThemes(sortedThemes);
      }
    } catch (error) {
      if (isComponentMountedRef.current) {
        showToast(texts.loadError, { success: false });
      }
    } finally {
      if (isComponentMountedRef.current) {
        setLoadingThemes(false);
      }
    }
  }, [setLoadingThemes, setThemes, showToast, isComponentMountedRef]);

  useEffect(() => {
    fetchThemes();
  }, [fetchThemes]);

  // Function to handle start date change
  const handleStartDateChange = (newStartDate) => {
    setStartDate(newStartDate);
    // If end date is before the new start date, update end date to start date
    if (endDate && newStartDate && endDate.isBefore(newStartDate)) {
      setEndDate(newStartDate);
    }
  };

  // Function to handle end date change with validation
  const handleEndDateChange = (newEndDate) => {
    if (newEndDate && startDate && newEndDate.isBefore(startDate)) {
      return;
    }
    setEndDate(newEndDate);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const themeTitleTrimmed = themeTitle.trim();
      const themeDescriptionTrimmed = themeDescription.trim();

      if (themeIdToEdit) {
        const theme = themes.find((t) => t.id === themeIdToEdit);
        await theme.updateTheme(startDate, endDate, themeTitleTrimmed, themeDescriptionTrimmed);
      } else {
        await CheckInTheme.create(startDate, endDate, themeTitleTrimmed, themeDescriptionTrimmed);
      }
      showToast(texts.saveSuccess, { success: true });
      resetForm();
      fetchThemes();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          themeIdToEdit,
          themeTitle,
          themeDescription,
          startDate,
          endDate,
          userId,
        },
      });
      showToast(texts.saveError, { error: true });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = useCallback((theme) => {
    setThemeIdToEdit(theme.id);
    setThemeTitle(theme.title);
    setThemeDescription(theme.description);
    setStartDate(moment(theme.startDate.toDate()));
    setEndDate(moment(theme.endDate.toDate()));
  }, [setThemeIdToEdit, setThemeTitle, setThemeDescription, setStartDate, setEndDate]);

  // Function to open the confirmation modal
  const handleDeleteClick = useCallback((themeId) => {
    setThemeToDeleteId(themeId);
    setConfirmDeleteModal(true);
  }, [setThemeToDeleteId, setConfirmDeleteModal]);

  const handleConfirmDelete = useCallback(async () => {
    setConfirmDeleteModal(false);
    if (!themeToDeleteId) return;

    try {
      const theme = themes.find((t) => t.id === themeToDeleteId);
      await theme.archive(userId);
      showToast(texts.deleteSuccess, { success: true });
      fetchThemes();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          themeToDeleteId,
          userId,
        },
      });
      showToast(texts.deleteError, { success: false });
    } finally {
      setThemeToDeleteId(null);
    }
  }, [setConfirmDeleteModal, themeToDeleteId, showToast, fetchThemes, setThemeToDeleteId, themes, userId]);

  const isThemeActive = (theme) => (
    theme.startDate.toDate().getTime() <= Date.now() && theme.endDate.toDate().getTime() >= Date.now()
  );

  return (
    <>
      <Container>
        {/* Left Panel: Form */}
        <FormPanel>
          <DatePickerContainer>
            <DatePicker
              label={texts.startDate}
              value={startDate}
              onChange={handleStartDateChange}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  size="medium"
                />
              )}
            />
            <DatePicker
              label={texts.endDate}
              value={endDate}
              onChange={handleEndDateChange}
              minDate={startDate}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  size="medium"
                />
              )}
            />
          </DatePickerContainer>
          <StyledTextField
            value={themeTitle}
            onChange={(e) => setThemeTitle(e.target.value)}
            label="Title"
            variant="outlined"
            size="medium"
            fullWidth
          />
          <StyledTextarea
            value={themeDescription}
            onChange={(e) => setThemeDescription(e.target.value)}
            minRows={10}
            placeholder="Description"
          />
          <ButtonContainer>
            <StyledButton
              variant="contained"
              color="primary"
              disabled={
                isLoading
                || !themeTitle.trim()
                || !themeDescription.trim()
                  || !startDate
                  || !endDate
                  || (endDate && startDate && endDate.isBefore(startDate))
              }
              onClick={handleSave}
              loading={isLoading}
              loadingPosition="end"
              endIcon={<Save />}
            >
              {themeIdToEdit ? texts.edit : texts.save}
            </StyledButton>
            {themeIdToEdit && (
              <StyledButton
                variant="contained"
                color="primary"
                disabled={isLoading}
                onClick={resetForm}
                loading={isLoading}
              >
                {texts.resetForm}
              </StyledButton>
            )}
          </ButtonContainer>
        </FormPanel>

        {/* Right Panel: Existing Themes */}
        <ThemesPanel>
          {themes.length === 0 && (
            <EmptyState>
              {isLoadingThemes ? texts.loadingThemes : texts.noThemes}
            </EmptyState>
          )}
          {themes.map((theme) => {
            const isActive = isThemeActive(theme);
            return (
              <ThemeCard key={theme.id} isActive={isActive}>
                <ThemeCardActionsRow>
                  <ThemeDate variant="body2">
                    {`${moment(theme.startDate.toDate()).format(DateFormat.DEFAULT_DATE_FORMAT)}
                    - ${moment(theme.endDate.toDate()).format(DateFormat.DEFAULT_DATE_FORMAT)}`}
                  </ThemeDate>
                  {isActive && <ActiveChip>{texts.active}</ActiveChip>}
                  <ActionButtons>
                    <EditButton
                      size="small"
                      onClick={() => handleEditClick(theme)}
                      disabled={isLoading}
                    >
                      <Edit fontSize="small" />
                    </EditButton>
                    <DeleteButton
                      size="small"
                      onClick={() => handleDeleteClick(theme.id)}
                      disabled={isLoading}
                      color="error"
                    >
                      <Delete fontSize="small" />
                    </DeleteButton>
                  </ActionButtons>
                </ThemeCardActionsRow>
                <ThemeTitle variant="subtitle1">{theme.title}</ThemeTitle>
                <ThemeDescription variant="body2">{theme.description}</ThemeDescription>
              </ThemeCard>
            );
          })}
        </ThemesPanel>
      </Container>

      {/* Confirmation Dialog for Delete */}
      <ConfirmDialog
        isOpen={showConfirmDeleteModal}
        dialogTexts={{
          title: themeToDeleteId ? texts.deleteTheme : '',
          content: themeToDeleteId ? texts.deleteConfirmation : '',
        }}
        confirmText={texts.deleteTheme}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setConfirmDeleteModal(false);
          setThemeToDeleteId(null);
        }}
        isLoading={isLoading}
        isDestructive
      />
    </>
  );
};

export default CheckInThemes;
